import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Company } from 'src/pages/billing/types/billing.types';
import { Building2, Check, ChevronDown, Lightbulb } from 'lucide-react';
import { fuzzyFilterSimilarity } from 'src/services/fuzzy-match';
import { useUpdateAccountMutation } from '../endpoints/accounts.endpoints';
import { sortAsc } from '../utils';
import { Campaign } from '../types/client-management.types';

interface CompanySelectorProps {
  campaign?: Campaign;
  companies: Array<Company>;
  attentionNeeded?: boolean;
  accountId: string;
  companyId: string;
  accountName: string;
  campaignName: string;
  onCompanySelect?: (companyId: string) => void;
  className?: string;
//   isLoading?: boolean;
}

const CompanySelector: React.FC<CompanySelectorProps> = ({
  campaign,
  attentionNeeded = false,
  accountId,
  companyId,
  accountName,
  campaignName,
  companies,
  onCompanySelect,
  className,
//   isLoading = false,
}) => {
  const [selectedCompany, setSelectedCompany] = useState<string>(companyId || '');
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>(companies);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const [updateAccount, { isLoading }] = useUpdateAccountMutation();

  // Calculate dropdown position for portal (fixed = viewport coords; avoids clipping by overflow)
  useEffect(() => {
    if (showDropdown && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 280),
      });
    } else {
      setDropdownPosition(null);
    }
  }, [showDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  // Smart suggestion logic based on name matching
  const suggestedCompanies = useMemo(() => {
    if (companies.length === 0) return [];
    if (campaign) {
      const company = companies.find(c => c.id === campaign.account.company);
      if (company) {
        return [company];
      }
    }
    const results = fuzzyFilterSimilarity(companies, accountName, (company: Company) => company.company_name, { limit: 3, threshold: 20 });
    return results.map((result) => result.item).slice(0, 1);
  }, [companies, accountName]);

  const handleCompanySelect = (selectedCompanyId: string) => {
    setSelectedCompany(selectedCompanyId);
    setShowDropdown(false);
    if (onCompanySelect) {
      onCompanySelect(selectedCompanyId);
    }
  };

  const handleDropdownToggle = () => {
    setShowDropdown(!showDropdown);
  };

  const handleSearch = (searchQuery: string) => {
    setSearchQuery(searchQuery);
    setFilteredCompanies(companies.filter(company => company.company_name && company.company_name.toLowerCase().includes(searchQuery && searchQuery.toLowerCase())));
  };

  const dropdownContent =
    showDropdown &&
    dropdownPosition &&
    (() => {
      const content = (
        <div
          ref={dropdownRef}
          className="fixed z-[9999] bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
          }}
        >
          <div className="p-1">
            <div className="px-3 py-2 text-xs text-gray-500 font-medium border-b border-gray-100">
              <input
                type="text"
                placeholder="Search companies"
                className="w-full text-sm text-gray-500 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                autoFocus
              />
            </div>
            {filteredCompanies.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                No companies available
              </div>
            ) : (
              sortAsc(filteredCompanies, 'company_name').map((company) => (
                <button
                  key={company.id}
                  onClick={() => handleCompanySelect(company.id)}
                  className={`w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm transition-colors duration-150 flex items-center gap-2 ${
                    selectedCompany === company.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                >
                  <Building2 className="size-3 flex-shrink-0" />
                  <span className="truncate">
                    {company.company_name}
                  </span>
                  {selectedCompany === company.id && (
                    <div className="ml-auto">
                      <div className="w-2 h-2 bg-blue-500 rounded-full" />
                    </div>
                  )}
                </button>
              ))
            )}
            {companyId && (
              <>
                <div className="border-t border-gray-100 mt-1 pt-1">
                  <button
                    onClick={() => handleCompanySelect('')}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm transition-colors duration-150 text-gray-500 flex items-center gap-2"
                  >
                    <div className="size-3 flex-shrink-0 border border-gray-300 rounded" />
                    <span>
                      Unassign Company
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      );
      return content;
    })();

  return (
    <div className="relative max-w-full">
      <div
        ref={triggerRef}
        onClick={!isLoading ? handleDropdownToggle : undefined}
        className={`text-xs text-gray-500 flex flex-row mt-2 w-fit max-w-full px-2 py-1 rounded-full items-center ${
          isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 cursor-pointer'
        }`}
      >
        <Building2 className={`size-4 ${isLoading ? '' : 'cursor-pointer'} ${attentionNeeded ? 'text-red-500' : 'text-gray-800'}`} />
        <div
          className={`text-sm font-normal text-gray-800 ml-2 items-center truncate max-w-32 
            ${isLoading ? '' : 'cursor-pointer'}
            ${attentionNeeded ? 'text-red-500 !font-bold' : 'text-gray-800'}
            `}
          title={companyId ? companies.find(c => c.id === companyId)?.company_name : 'Unassigned'}
        >
          {isLoading ? 'Updating...' : companyId ? companies.find(c => c.id === companyId)?.company_name : 'Unassigned'}
        </div>
        <ChevronDown className={`size-4 ml-2 ${isLoading ? '' : 'cursor-pointer'}`} />
      </div>
      {!companyId && suggestedCompanies && suggestedCompanies.length > 0 && (
        <>
          <div
            onClick={() => handleCompanySelect(suggestedCompanies[0].id)}
            className='flex w-full text-sm text-gray-500 ml-2 cursor-pointer underline items-center'
          >
            <Lightbulb className='size-3 mr-1 bg-yellow-500 rounded-full p-0.5 text-white' />
            {suggestedCompanies[0].company_name}
          </div>
        </>
      )}
      {attentionNeeded && companyId && (
        <div
          onClick={() => handleCompanySelect(companyId)}
          className='flex w-full text-sm text-gray-500 ml-2 cursor-pointer underline items-center'
        >
          <Check className='size-3 mr-1 bg-green-501 rounded-full p-0.5 text-white' />
          Verify
        </div>
      )}
      {typeof document !== 'undefined' && dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default CompanySelector;
